import { useMemo, useState } from 'react'
import { Button, EmptyState, PageHeader, Segmented, TextInput } from '../../../shared/ui'
import KartenStatusBadge from './KartenStatusBadge'
import { datumDe, kartenFehler, kartenZustand, useAllSchoolCards, type KartenZustand, type SchoolCard } from './kartenApi'
import { printSchoolCards } from './printSchoolCards'

type Filter = 'alle' | 'aktiv' | 'gesperrt' | 'abgelaufen' | 'bald'

const OPTIONS = [
  { value: 'alle', label: 'Alle' },
  { value: 'aktiv', label: 'Aktiv' },
  { value: 'gesperrt', label: 'Gesperrt' },
  { value: 'abgelaufen', label: 'Abgelaufen' },
  { value: 'bald', label: 'Läuft in 30 Tagen ab' },
] as const

// „Aktiv" im Filter = nutzbar (auch wenn sie bald abläuft).
const passt = (f: Filter, z: KartenZustand) =>
  f === 'alle' || (f === 'aktiv' ? z === 'aktiv' || z === 'bald' : f === z)

export default function KartenPage() {
  const { data, isLoading, error } = useAllSchoolCards()
  const [filter, setFilter] = useState<Filter>('alle')
  const [suche, setSuche] = useState('')

  const rows = useMemo(() => {
    const q = suche.trim().toUpperCase()
    return (data?.karten ?? [])
      .filter((k) => (!q || k.chip.includes(q)) && passt(filter, kartenZustand(k)))
      .sort((a, b) => (a.schoolName || '').localeCompare(b.schoolName || '', 'de') || a.bezeichnung.localeCompare(b.bezeichnung, 'de'))
  }, [data, filter, suche])

  return (
    <div>
      <PageHeader title="Schulkarten" subtitle={data ? `${data.karten.length} Karten` : undefined} />

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="w-56">
          <TextInput value={suche} onChange={(e) => setSuche(e.target.value)} placeholder="Nummer suchen…" leadingIcon="search" mono />
        </div>
        <Segmented<Filter> value={filter} onChange={setFilter} options={OPTIONS} size="xs" />
      </div>

      {isLoading ? (
        <p className="text-sm text-on-surface-variant">Karten werden geladen…</p>
      ) : error ? (
        <p className="text-sm text-rose-600">{kartenFehler(error)}</p>
      ) : rows.length === 0 ? (
        <EmptyState icon="badge" title="Keine Karten gefunden" description="Karten werden in der Schule unter „Karten“ angelegt." />
      ) : (
        <div className="rounded-xl border border-outline-variant/40 bg-surface-container-lowest divide-y divide-outline-variant/20 overflow-hidden">
          {rows.map((k: SchoolCard, i) => {
            const neueSchule = i === 0 || rows[i - 1].schoolId !== k.schoolId
            return (
              <div key={k.id}>
                {neueSchule && (
                  <div className="px-4 py-1.5 bg-surface-container-low/60 text-[11px] font-semibold text-on-surface-variant">
                    {k.schoolName}{k.kuerzel ? ` · ${k.kuerzel}` : ''}
                  </div>
                )}
                <div className="grid grid-cols-1 md:grid-cols-[110px_1fr_150px_230px_auto] gap-3 items-center px-4 py-2.5">
                  <span className="font-mono text-sm font-semibold tabular-nums">{k.chip}</span>
                  <span className="text-sm truncate">{k.bezeichnung}</span>
                  <KartenStatusBadge zustand={kartenZustand(k)} />
                  <span className="text-xs text-on-surface-variant tabular-nums">
                    {datumDe(k.gueltigVon)} – {k.gueltigBis ? datumDe(k.gueltigBis) : 'unbefristet'}
                  </span>
                  <Button variant="ghost" size="sm" icon="print" onClick={() => printSchoolCards([k])}>Drucken</Button>
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
